'use client';

import React, { useState } from 'react';
import {
  ApiErrorResponse,
  ErrorCategory,
  ErrorAlertProps,
  isApiErrorResponse,
  getErrorCategoryMessage,
} from '@/app/types/errors';

/**
 * ============================================
 * MANDATORY ERROR DISPLAY COMPONENT
 * ============================================
 *
 * ALL error messages in SportStack MUST use this component.
 * Do NOT create custom error displays or use raw Alert components.
 *
 * Usage:
 *   import ErrorAlert from '@/app/components/ErrorAlert';
 *
 *   // With API error response
 *   <ErrorAlert error={apiError} onRetry={handleRetry} />
 *
 *   // With simple string
 *   <ErrorAlert error="Something went wrong" />
 *
 *   // With Error object
 *   <ErrorAlert error={new Error("Failed")} onDismiss={() => setError(null)} />
 */

// Determine if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Map error category to Tailwind color scheme
 */
function getCategoryColors(category?: ErrorCategory): {
  bg: string;
  border: string;
  text: string;
  title: string;
  icon: string;
  button: string;
  buttonHover: string;
} {
  switch (category) {
    case ErrorCategory.VALIDATION:
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        title: 'text-amber-800',
        icon: '⚠️',
        button: 'bg-amber-600',
        buttonHover: 'hover:bg-amber-700',
      };
    case ErrorCategory.AUTHENTICATION:
    case ErrorCategory.AUTHORIZATION:
      return {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-700',
        title: 'text-orange-800',
        icon: '🔒',
        button: 'bg-orange-600',
        buttonHover: 'hover:bg-orange-700',
      };
    case ErrorCategory.NOT_FOUND:
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        title: 'text-blue-800',
        icon: '🔍',
        button: 'bg-blue-600',
        buttonHover: 'hover:bg-blue-700',
      };
    case ErrorCategory.NETWORK:
    case ErrorCategory.TIMEOUT:
      return {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-700',
        title: 'text-purple-800',
        icon: '🌐',
        button: 'bg-purple-600',
        buttonHover: 'hover:bg-purple-700',
      };
    default:
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        title: 'text-red-800',
        icon: '❌',
        button: 'bg-red-600',
        buttonHover: 'hover:bg-red-700',
      };
  }
}

/**
 * Parse any error format into ApiErrorResponse structure
 */
function parseError(error: ApiErrorResponse | Error | string | null): ApiErrorResponse | null {
  if (!error) return null;

  if (typeof error === 'string') {
    return {
      error_code: ErrorCategory.INTERNAL,
      message: error,
      timestamp: new Date().toISOString(),
    };
  }

  if (error instanceof Error) {
    // Check if it has an attached apiError (from throwOnApiError)
    const apiError = (error as Error & { apiError?: ApiErrorResponse }).apiError;
    if (apiError) return apiError;

    return {
      error_code: ErrorCategory.INTERNAL,
      message: error.message,
      timestamp: new Date().toISOString(),
    };
  }

  if (isApiErrorResponse(error)) {
    return error;
  }

  return {
    error_code: ErrorCategory.INTERNAL,
    message: 'Errore sconosciuto',
    timestamp: new Date().toISOString(),
  };
}

export default function ErrorAlert({
  error,
  title,
  showTechnicalDetails = isDevelopment,
  onRetry,
  onDismiss,
  className = '',
}: ErrorAlertProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const errorData = parseError(error);
  if (!errorData) return null;

  const colors = getCategoryColors(errorData.error_code);
  const displayTitle = title || getErrorCategoryMessage(errorData.error_code);

  const hasTechnicalInfo =
    showTechnicalDetails &&
    (errorData.technical_details ||
      errorData.stack_trace ||
      errorData.request_id ||
      errorData.path);

  return (
    <div
      className={`${colors.bg} ${colors.border} border rounded-lg p-4 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        {/* Icon */}
        <div className="flex-shrink-0">
          <span className="text-xl" aria-hidden="true">
            {colors.icon}
          </span>
        </div>

        {/* Content */}
        <div className="ml-3 flex-1">
          {/* Title */}
          <h3 className={`text-sm font-medium ${colors.title}`}>{displayTitle}</h3>

          {/* Main message — o, se il backend li manda, TUTTI i motivi, uno per
              riga (testo suo; coprono anche i campi, niente doppioni sotto). */}
          {errorData.reasons && errorData.reasons.length > 1 ? (
            <ul className={`mt-1 list-disc list-inside text-sm space-y-0.5 ${colors.text}`} data-testid="error-reasons">
              {errorData.reasons.map((reason, idx) => (
                <li key={idx}>{reason}</li>
              ))}
            </ul>
          ) : (
            <div className={`mt-1 text-sm ${colors.text}`}>{errorData.message}</div>
          )}

          {/* Field errors (validation) */}
          {!errorData.reasons && errorData.field_errors && errorData.field_errors.length > 0 && (
            <div className="mt-2">
              <p className={`text-xs font-semibold ${colors.title}`}>Campi con errori:</p>
              <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                {errorData.field_errors.map((fieldError, idx) => (
                  <li key={idx} className={colors.text}>
                    <strong>{fieldError.field}</strong>: {fieldError.message}
                    {fieldError.value !== undefined && (
                      <span className="opacity-75"> (valore: {String(fieldError.value)})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Technical details (dev only) */}
          {hasTechnicalInfo && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setDetailsOpen(!detailsOpen)}
                className={`text-xs ${colors.text} underline hover:no-underline focus:outline-none`}
              >
                {detailsOpen ? '▼ Nascondi dettagli tecnici' : '▶ Mostra dettagli tecnici'}
              </button>

              {detailsOpen && (
                <div className="mt-2 p-2 bg-white/50 rounded text-xs font-mono space-y-1">
                  {errorData.request_id && (
                    <p>
                      <strong>Request ID:</strong> {errorData.request_id}
                    </p>
                  )}
                  {errorData.path && (
                    <p>
                      <strong>Endpoint:</strong> {errorData.method} {errorData.path}
                    </p>
                  )}
                  {errorData.technical_details && (
                    <p>
                      <strong>Dettagli:</strong> {errorData.technical_details}
                    </p>
                  )}
                  {errorData.timestamp && (
                    <p>
                      <strong>Timestamp:</strong> {new Date(errorData.timestamp).toLocaleString('it-IT')}
                    </p>
                  )}
                  {errorData.stack_trace && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs">Stack trace</summary>
                      <pre className="mt-1 p-1 bg-gray-100 rounded text-[10px] overflow-auto max-h-32">
                        {errorData.stack_trace}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          {(onRetry || onDismiss) && (
            <div className="mt-3 flex space-x-2">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className={`px-3 py-1.5 ${colors.button} text-white text-sm rounded-md ${colors.buttonHover} focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors`}
                >
                  Riprova
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className={`px-3 py-1.5 bg-white ${colors.text} text-sm border ${colors.border} rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors`}
                >
                  Chiudi
                </button>
              )}
            </div>
          )}

          {/* Help link */}
          {errorData.help_url && (
            <a
              href={errorData.help_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-2 inline-block text-xs ${colors.text} underline hover:no-underline`}
            >
              Consulta la documentazione →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
